import { ApiError, api } from '@/core/network/api';
import { authStore, type AuthUser } from '@/core/state/auth-store';

/**
 * The consent purposes, mirrored from the backend's Prisma enum.
 *
 * Kept as a string union rather than imported: the frontend has no Prisma
 * client, and the wire values are the contract. The two arrays below mirror
 * `REQUIRED_CONSENT_PURPOSES` / `OPTIONAL_CONSENT_PURPOSES` in
 * `backend/src/game/consent/consent.types.ts`.
 */
export type ConsentPurpose =
  | 'TERMS_OF_SERVICE'
  | 'PRIVACY_POLICY'
  | 'MARKETING_EMAIL'
  | 'ANALYTICS';

/** Must be accepted before an account can exist. */
export const REQUIRED_PURPOSES = ['TERMS_OF_SERVICE', 'PRIVACY_POLICY'] as const;
export type RequiredPurpose = (typeof REQUIRED_PURPOSES)[number];

/** Offered separately, and never a condition of registering. */
export const OPTIONAL_PURPOSES = ['MARKETING_EMAIL', 'ANALYTICS'] as const;
export type OptionalPurpose = (typeof OPTIONAL_PURPOSES)[number];

/** `LegalDocumentRef` from `GET /api/consent/documents`. */
export interface LegalDocumentRef {
  readonly id: string;
  readonly slug: string;
  readonly version: number;
  readonly purpose: ConsentPurpose | null;
  readonly title: string;
  readonly contentHash: string;
  readonly locale: string | null;
  readonly effectiveFrom: string;
  readonly supersededAt: string | null;
  readonly publishedAt: string;
}

/** The same, with the frozen body — `GET /api/consent/documents/:purpose`. */
export interface LegalDocumentSnapshot extends LegalDocumentRef {
  readonly content: string;
}

/** What the screen holds for each required document while the form is open. */
export type RequiredDocuments = Readonly<Record<RequiredPurpose, LegalDocumentSnapshot>>;

export interface AcceptedDocument {
  readonly purpose: RequiredPurpose;
  readonly documentVersion: number;
  readonly documentHash: string;
}

export interface RegisterInput {
  readonly email: string;
  readonly password: string;
  readonly confirmPassword: string;
  readonly username?: string;
  readonly altcha: string;
  readonly acceptedDocuments: readonly AcceptedDocument[];
  readonly optionalConsents?: readonly OptionalPurpose[];
  readonly locale?: string;
}

export interface RegisterResponse {
  readonly user: AuthUser;
  readonly consents: readonly {
    purpose: ConsentPurpose;
    documentSlug: string | null;
    documentVersion: number | null;
    documentHash: string | null;
  }[];
}

/**
 * Fetch the frozen snapshot of every required document.
 *
 * THIS, NOT `GET /api/cms/legal/:slug`. The CMS route serves the live, editable
 * page; this serves the version whose hash the consent record will carry.
 * Displaying one and recording the other would put a claim in the audit trail
 * that the user agreed to text they were never shown.
 *
 * Resolves `null` for a purpose with nothing published (the endpoint 404s).
 * That is an operator error, not a user error, and the screen says so.
 */
export async function fetchRequiredDocuments(
  signal?: AbortSignal,
): Promise<Readonly<Record<RequiredPurpose, LegalDocumentSnapshot | null>>> {
  const entries = await Promise.all(
    REQUIRED_PURPOSES.map(async (purpose) => {
      try {
        const doc = await api.get<LegalDocumentSnapshot>(
          `/consent/documents/${purpose}`,
          signal === undefined ? undefined : { signal },
        );
        return [purpose, doc] as const;
      } catch (err) {
        // A 404 means "not published yet" — a state the screen must render, not
        // an exception. Anything else is a real failure and is rethrown, so a
        // network outage is not misreported as "the operator forgot".
        if (err instanceof ApiError && err.status === 404) return [purpose, null] as const;
        throw err;
      }
    }),
  );
  return Object.fromEntries(entries) as Record<RequiredPurpose, LegalDocumentSnapshot | null>;
}

/**
 * Create the account AND record the consent, in one request.
 *
 * `POST /api/game/auth/register` is the game's own route (see
 * `backend/src/game/auth/game-auth.controller.ts` for why it exists rather than
 * the client calling core's `/auth/register` and then `/api/consent`: two calls
 * leave a live account, logged in, that has agreed to nothing, and any dropped
 * connection between them makes that permanent).
 *
 * Everything else in the flow is core's: `/auth/altcha-challenge` mints the
 * captcha this verifies, `/auth/verify-email` confirms the address, and
 * `/auth/login` signs in afterwards.
 */
export async function registerWithConsent(input: RegisterInput): Promise<AuthUser> {
  authStore.set((s) => ({ ...s, status: 'authenticating', error: null }));
  try {
    const res = await api.post<RegisterResponse>('/game/auth/register', {
      email: input.email,
      password: input.password,
      confirmPassword: input.confirmPassword,
      // Omitted rather than sent as '' — the DTO's @Length(3, 32) would reject
      // an empty string, and "no username" is a legitimate registration.
      ...(input.username !== undefined && input.username.length > 0
        ? { username: input.username }
        : {}),
      altcha: input.altcha,
      acceptedDocuments: input.acceptedDocuments,
      ...(input.optionalConsents !== undefined && input.optionalConsents.length > 0
        ? { optionalConsents: input.optionalConsents }
        : {}),
      ...(input.locale !== undefined ? { locale: input.locale } : {}),
    });
    authStore.set({ user: res.user, status: 'authenticated', error: null });
    return res.user;
  } catch (e) {
    authStore.set((s) => ({ ...s, status: 'error', error: (e as Error).message }));
    throw e;
  }
}
